<template>
  <ValidationObserver v-slot="{ passes }" ref="observer">
    <v-dialog
      v-model="dialog"
      max-width="520px"
      :fullscreen="$vuetify.breakpoint.mdAndDown || dialogFullScreen"
      :hide-overlay="$vuetify.breakpoint.mdAndDown"
      scrollable
      no-click-animation>
      <v-card>
        <v-toolbar dark color="primary" :max-height="$vuetify.breakpoint.mdAndUp ? 64 : 56">
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.preferences.title') }}</v-toolbar-title>
          <v-progress-linear :active="saveLoading" :indeterminate="saveLoading" absolute bottom color="lime" />
          <div class="flex-grow-1" />
          <v-toolbar-items>
            <v-btn v-if="$vuetify.breakpoint.mdAndUp" icon @click="dialogFullScreen = !dialogFullScreen">
              <v-icon v-if="dialogFullScreen === true">mdi-fullscreen-exit</v-icon>
              <v-icon v-else>mdi-fullscreen</v-icon>
            </v-btn>
            <v-btn dark text :disabled="saveLoading" @click="passes(submit)">
              <v-icon>mdi-content-save-outline</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Encarts: Avis (ratings) settings -->
          <v-card outlined class="mb-6">
            <v-card-title class="subtitle-1 font-weight-medium">
              {{ $t('components.preferences.sections.ratings') }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-radio-group
                v-model="itemsPerPage"
                :label="$t('components.account.pagination')"
                :disabled="saveLoading"
                class="mt-0"
              >
                <v-radio
                  v-for="opt in itemsPerPageOptions"
                  :key="opt"
                  :label="String(opt)"
                  :value="opt"
                  color="primary"
                  class="mr-2 ml-2"
                ></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>

          <!-- Encarts: General settings -->
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-medium">
              {{ $t('components.preferences.sections.general') }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-radio-group
                v-model="darkMode"
                :label="$t('views.layout.color_mode')"
                :disabled="saveLoading"
                class="mt-0"
                @change="onDarkModeChange"
              >
                <v-radio
                  :label="$t('views.layout.light_mode')"
                  :value="0"
                  color="primary"
                ></v-radio>
                <v-radio
                  :label="$t('views.layout.night_mode')"
                  :value="1"
                  color="primary"
                ></v-radio>
                <v-radio
                  :label="$t('views.layout.system_preferences')"
                  :value="2"
                  color="primary"
                ></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import AuthLogic from '@/logics/AuthLogic'
import ModelLogic from '@/logics/ModelLogic'
import { EventBus } from '@/services/Request'

export default {
  name: 'PreferencesDialog',
  components: { ValidationObserver },
  data: () => ({
    dialog: false,
    dialogFullScreen: false,
    saveLoading: false,
    itemsPerPageOptions: [],
    itemsPerPage: null,
    darkMode: 0,
  }),
  methods: {
    openDialog () {
      return new Promise(resolve => {
        this.initFromUser()
        this.dialog = true
        resolve()
      })
    },
    closeDialog () {
      return new Promise(resolve => {
        this.dialog = false
        resolve()
      })
    },
    initFromUser () {
      try {
        // Options from env (string like "10,25,50,100")
        const raw = '15,30,50,100'
        this.itemsPerPageOptions = raw.split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n))

        const user = AuthLogic.user()
        // Fallbacks
        const defaultIPP = (user && user.items_per_page) ? user.items_per_page : (this.itemsPerPageOptions[0] || 50)

        const metas = (user && Array.isArray(user.metas)) ? user.metas : []

        // Items per page: prefer direct user field if present; fallback to meta if backend exposes it as meta
        const metaIpp = metas.find(m => m.key === 'items_per_page')
        this.itemsPerPage = metaIpp && metaIpp.value ? parseInt(metaIpp.value, 10) : defaultIPP
        if (isNaN(this.itemsPerPage)) this.itemsPerPage = defaultIPP

        // Dark mode: from user.has_dark_mode (0 light, 1 dark, 2 system)
        this.darkMode = (user && user.has_dark_mode !== undefined) ? user.has_dark_mode : 0
      } catch (e) {
        // conservative defaults
        this.itemsPerPage = 50
        this.darkMode = 0
      }
    },
    onDarkModeChange (val) {
      try {
        if (val !== 2) {
          this.$vuetify.theme.dark = !!val
        } else {
          const mq = window.matchMedia('(prefers-color-scheme: dark)')
          this.$vuetify.theme.dark = mq.matches
        }
      } catch (e) {
        // ignore theme application errors
      }
    },
    async submit () {
      this.saveLoading = true
      try {
        // Persist dark mode preference first (dedicated endpoint)
        // await ModelLogic.updateOne('users', AuthLogic.user().id + '/darkmode', { 'has_dark_mode': this.darkMode })

        // Persist other preferences
        await ModelLogic.updateOne('users', AuthLogic.user().id, {
          'items_per_page': this.itemsPerPage,
          'has_dark_mode': this.darkMode,
        })

        await AuthLogic.loadUser()
        this.closeDialog()
        EventBus.$emit('show-snackbar', { text: this.$t('components.preferences.saved'), timeout: 2500 })
        EventBus.$emit('user-account-updated', { type: 'preferences' })
      } catch (error) {
        console.error(error)
        EventBus.$emit('show-snackbar', { text: this.$t('components.preferences.save_error'), color: 'error', timeout: 4000 })
      } finally {
        this.saveLoading = false
      }
    }
  }
}
</script>
